import React, { useEffect, useState, useContext } from 'react'
import PropTypes from 'prop-types'
import { Input, Button, Icon, SectionTitle } from '@pinflag/pinflag-ui-kit'
import { useForm } from 'react-hook-form'
import classNames from 'classnames'
import { InputValidator } from '../helpers/Validator'
import { DataContext } from '../context/DataContext'
import { prettifyRut } from 'react-rut-formatter'
import { useVtex } from '../hook/useVtex'
import { useShopify } from '../hook/useShopify'
import { scrollToBottom, onlyLetters, onlyNumbers, onlySomeNumbers, noSpecialCharacters } from '../helpers/utils'
import { setUserInfoLocalStorage } from '../helpers/localStorage'
import { useCmsAdapter } from '../hook/useCmsAdapter'
import { gaCompletedUserInfoPostMessage } from '../helpers/gaEventsHelper'
import { CUSTOM_BILLING_BUTTON, TAX_DOCUMENT_SELECTOR_STORES } from '../constants/stores'
import { TAX_DOCUMENT_TYPES } from '../constants/translation'

const DISABLED_LOOK_STYLE = {
  backgroundColor: '#f1f5f9',
  color: '#6b7280',
  cursor: 'not-allowed'
}

const UserForm = ({ className }) => {
  const {
    userInfo: userData,
    billingInfo: billingData,
    setIsPickup,
    setUserInfo,
    steps,
    setSteps,
    setCurrentStep,
    setBillingInfo,
    ecommerceInfo,
    companyInfo,
    taxDocumentType,
    setTaxDocumentType
  } = useContext(DataContext)
  const { primaryColor, secondaryColor, companyName, useBillingData } = companyInfo
  const { saveUserInformationVtex } = useVtex()
  const { handleUserInformationShopify } = useShopify()
  const { postTrackingEvent } = useCmsAdapter()
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors }
  } = useForm({
    mode: 'all'
  })

  const isShopify = ecommerceInfo.cms === 'shopify'
  const [saveData, setSaveData] = useState(userData.saveData)
  const [customErrors, setCustomErrors] = useState({})
  const [loadingButton, setLoadingButton] = useState(false)
  const [isBilling, setIsBilling] = useState(
    !!billingData.corporateName
  )

  const showTaxDocumentSelector = TAX_DOCUMENT_SELECTOR_STORES.includes(companyName)

  if (showTaxDocumentSelector) {
    register('taxDocumentType', { required: 'Campo requerido' })
  }

  const selectedTaxDocumentType = watch('taxDocumentType')
  const missingTaxDocumentType = showTaxDocumentSelector && !selectedTaxDocumentType

  const handleTaxDocumentTypeChange = (value) => {
    setValue('taxDocumentType', value, { shouldValidate: true })
    setTaxDocumentType(value)
    setIsBilling(value === 'invoice')
  }

  const handleAutoSave = async (email) => {
    const data = {
      email,
      names: '',
      phone: '',
      rut: '',
      saveData: false
    }
    const wrongEmail = await handleUserInformationShopify(data)
    if (wrongEmail) {
      const emailError = {
        type: 'validate',
        message: 'Ingrese un dominio que exista'
      }
      setCustomErrors(prevState => {
        return { ...prevState, email: emailError }
      })
      setError('email', emailError)
    } else {
      setUserInfo(userData)
      isBilling && setBillingInfo(billingData)
      setUserInfoLocalStorage(userData)
      saveUserInformationVtex(data, isBilling ? billingData : false)
    }
  }
  const userInfo = {
    email: {
      ...register('email', {
        required: 'Campo requerido',
        validate: value =>
          InputValidator.isEmailValid(value) || 'Ingresa un correo válido',
        onBlur: (e) => {
          setCustomErrors({ ...customErrors, email: errors?.email })
          if (InputValidator.isEmailValid(e.target.value)) {
            handleAutoSave(e.target.value)
          }
        },
        onChange: e => setValue('email', noSpecialCharacters(e.target.value))
      }),
      label: 'Correo electrónico'
    },
    names: {
      ...register('names', {
        required: 'Campo requerido',
        validate: value =>
          InputValidator.isNameValid(value) || 'Nombre y/o Apellido inválidos',
        onBlur: () => setCustomErrors({ ...customErrors, names: errors?.names }),
        onChange: e => setValue('names', onlyLetters(e.target.value))
      }),
      label: 'Nombre y Apellido '
    },
    phone: {
      ...register('phone', {
        required: 'Campo requerido',
        validate: value =>
          InputValidator.isPhoneValid(value, ecommerceInfo.cms) || 'Ingresa un número válido',
        onBlur: () =>
          setCustomErrors({ ...customErrors, phone: errors?.phone }),
        onChange: e => setValue('phone', onlySomeNumbers(onlyNumbers(e.target.value)))
      }),
      label: 'Teléfono'
    },
    rut: {
      ...register('rut', {
        required: 'Campo requerido',
        validate: value => InputValidator.isRutValid(value) || 'Rut inválido',
        onBlur: () => setCustomErrors({ ...customErrors, rut: errors?.rut }),
        onChange: e => setValue('rut', prettifyRut(e.target.value))
      }),
      label: 'Rut'
    },
    saveData: { ...register('saveData', { required: companyInfo.companyName === 'SCJJSPA' ? 'Debes aceptar términos y condiciones' : false }) }
  }

  let billingInfo = {
    corporateName: {
      ...register('corporateName', {
        required: isBilling ? 'Campo requerido' : false,
        onBlur: () =>
          setCustomErrors({
            ...customErrors,
            corporateName: errors?.corporateName
          })
      }),
      label: 'Razón Social'
    },
    corporateRut: {
      ...register('corporateRut', {
        required: isBilling ? 'Campo requerido' : false,
        validate: value =>
          InputValidator.isRutValid(value) || !isBilling || 'Rut inválido',
        onBlur: () =>
          setCustomErrors({
            ...customErrors,
            corporateRut: errors?.corporateRut
          }),
        onChange: e => setValue('corporateRut', prettifyRut(e.target.value))
      }),
      label: 'Rut'
    },
    category: {
      ...register('category', {
        required: isBilling && companyName !== 'Vitel Energia' ? 'Campo requerido' : false,
        onBlur: () =>
          setCustomErrors({ ...customErrors, category: errors?.category })
      }),
      label: 'Giro'
    },
    corporatePhone: {
      ...register('corporatePhone', {
        required: isBilling && companyName !== 'Vitel Energia' ? 'Campo requerido' : false,
        validate: value =>
          InputValidator.isPhoneValid(value) ||
          !isBilling ||
          'Ingrese un número con 9 dígitos',
        onBlur: () =>
          setCustomErrors({
            ...customErrors,
            corporatePhone: errors?.corporatePhone
          }),
        onChange: e => setValue('corporatePhone', onlySomeNumbers(onlyNumbers(e.target.value)))
      }),
      label: 'Teléfono'
    }
  }

  const shopifyExtraBillingInfo = {
    address: {
      ...register('address', {
        required: isBilling && isShopify ? 'Campo requerido' : false,
        onBlur: () =>
          setCustomErrors({
            ...customErrors,
            address: errors?.address
          })
      }),
      label: 'Dirección'
    },
    district: {
      ...register('district', {
        required: isBilling && isShopify ? 'Campo requerido' : false,
        onBlur: () =>
          setCustomErrors({
            ...customErrors,
            district: errors?.district
          })
      }),
      label: 'Comuna'
    }
  }

  useBillingData && isShopify && Object.assign(billingInfo, shopifyExtraBillingInfo)

  if (companyName === 'Vitel Energia') {
    const { corporateName, corporateRut } = billingInfo
    billingInfo = { corporateName, corporateRut }
  }
  const containerClasses = classNames(
    'flex flex-col items-between justify-between h-full md:w-[495px] md:text-[11px]',
    className
  )

  const handleCheckbox = () => {
    setValue('saveData', !saveData)
    setSaveData(!saveData)
  }

  useEffect(() => {
    setValue('names', userData.names)
    setValue('email', userData.email)
    setValue('rut', userData.rut)
    setValue('phone', userData.phone)
    setValue('saveData', userData.saveData)
    setValue('corporateName', billingData.corporateName)
    setValue('corporateRut', billingData.corporateRut)
    setValue('category', companyName !== 'Vitel Energia' ? billingData.category : undefined)
    setValue('corporatePhone', companyName !== 'Vitel Energia' ? billingData.corporatePhone : undefined)
    useBillingData && isShopify && setValue('address', billingData.address)
    useBillingData && isShopify && setValue('district', billingData.district)
    if (billingData.corporateName) setIsBilling(true)
  }, [setValue, userData, billingData, companyInfo])

  useEffect(() => {
    if (!showTaxDocumentSelector) return
    setValue('taxDocumentType', taxDocumentType || undefined)
    if (taxDocumentType) setIsBilling(taxDocumentType === 'invoice')
  }, [setValue, showTaxDocumentSelector, taxDocumentType])

  useEffect(() => {
    isBilling && scrollToBottom()
  }, [isBilling])

  useEffect(() => {
    setIsPickup(false)
    if (companyInfo.companyName === 'Epic') { setIsPickup(true) }
  }, [setIsPickup])

  const handleUserFormSubmit = async formData => {
    const {
      names,
      email,
      rut,
      phone,
      saveData,
      category,
      corporateName,
      corporatePhone,
      corporateRut,
      address,
      district
    } = formData
    postTrackingEvent('click - submit', { userEmail: email })
    const userData = { names: names.trim(), email, rut, phone, saveData }
    const billingData = {
      category,
      corporateName,
      corporatePhone,
      corporateRut,
      address,
      district
    }
    gaCompletedUserInfoPostMessage()
    setLoadingButton(true)
    const wrongEmail = await handleUserInformationShopify(userData)
    if (wrongEmail) {
      const emailError = {
        type: 'validate',
        message: 'Ingrese un dominio que exista'
      }
      setCustomErrors(prevState => {
        return { ...prevState, email: emailError }
      })
      setError('email', emailError)
    } else {
      setUserInfo(userData)
      isBilling
        ? setBillingInfo(billingData)
        : setBillingInfo({
          corporateName: '',
          category: '',
          corporateRut: '',
          corporatePhone: '',
          address: '',
          district: ''
        })
      const updatedSteps = [...steps]
      updatedSteps[0].completed = true
      updatedSteps[1].disabled = false
      setSteps(updatedSteps)
      setCurrentStep(2)
      setUserInfoLocalStorage(userData)
      saveUserInformationVtex(userData, isBilling ? billingData : false)
    }
    setLoadingButton(false)
  }

  return (
    <div className={`grow h-full md:w-[537px] bg-slate-100 ${ecommerceInfo.cms === 'woocommerce' && 'md:bg-white'} pt-2`}>
      <div className='text-xs text-slate-500 pt-2 flex w-[100vw] justify-center md:hidden'>
        Completa tus datos para continuar con la compra.
      </div>
      <form
        onSubmit={handleSubmit(handleUserFormSubmit)}
        className={containerClasses}
        autoComplete='off'
        id='user-info-form'
      >
        <div
          className={`div-user-form ${useBillingData ? 'h-[382px]' : 'h-[352px]'
            } bg-white md:w-full md:rounded-lg p-4 mx-5 md:mt-3 md:p-0 md:px-[21px] md:h-[357px] overflow-auto ${ecommerceInfo.cms === 'woocommerce' && 'md:bg-slate-100'} max-[350px]:h-[300px]`}
        >
          <div className='flex items-center justify-between pl-2 pb-3 md:pb-2 md:my-1 md:mt-3 text-sm font-medium md:text-[11px] md:font-bold'>
            <SectionTitle
              title='Datos de identificación'
              iconName='user'
              primaryColor={primaryColor}
              iconCustomStyle={{ fill: 'transparent', stroke: 'white' }}
            />
            <div className={`${useBillingData && !showTaxDocumentSelector ? 'md:flex' : ''} hidden`}>
              <Button
                label={isBilling ? 'No usar datos de Facturación' : 'Agregar datos de Facturación'}
                LeftIcon={
                  <Icon name='edit' className={`mr-1 text-sm ${Object.keys(CUSTOM_BILLING_BUTTON).includes(companyName) && 'fill-white'}`} width={9} />
                }
                onClick={() => {
                  setIsBilling(!isBilling)
                }}
                className={`px-4 py-1 rounded-2xl text-[11px] font-light flex items-center h-[23px] ${ecommerceInfo.cms === 'woocommerce' && 'md:bg-white'} bg-slate-100`}
                testId='billing-data'
                customStyle={Object.keys(CUSTOM_BILLING_BUTTON).includes(companyName) ? { backgroundColor: CUSTOM_BILLING_BUTTON[companyName], color: 'white' } : {}}
              />
            </div>
          </div>
          <div className={'flex flex-wrap justify-between'}>
            {Object.keys(userInfo).map(key => {
              if (key === 'saveData') return false
              return (
                <Input
                  key={key}
                  {...userInfo[key]}
                  containerClass={`mb-3 md:mb-1.5 bg-white border md:bg-white ${key === 'phone'
                    ? 'md:w-7/12'
                    : key === 'rut'
                      ? 'md:w-2/5'
                      : ''
                    }`}
                  innerRef={userInfo[key].ref}
                  type={
                    key === 'email' ? 'email' : key === 'phone' ? 'tel' : 'text'
                  }
                  errors={errors[key] || watch(key) === ''}
                  customErrors={customErrors[key]}
                  secondaryColor={secondaryColor}
                  testId={key}
                />
              )
            })}
          </div>

          {showTaxDocumentSelector && (
            <div
              className={classNames(
                'w-full bg-white rounded-[10px] border-[0.5px] border-[#BECED1] p-[1px] mb-3 md:mb-1.5',
                { 'border border-green-200': !!selectedTaxDocumentType },
                { 'border border-red-300': !!errors.taxDocumentType }
              )}
            >
              <div className='relative flex flex-col justify-center px-[1.3rem] py-2.5 min-h-14'>
                <span className='text-xs font-light font-inter text-[#6B7F86]'>
                  Tipo de documento tributario
                </span>
                <div className='flex flex-row items-center gap-6 mt-1'>
                  {Object.keys(TAX_DOCUMENT_TYPES).map(key => (
                    <label
                      key={key}
                      className='flex items-center gap-2 text-sm font-inter cursor-pointer'
                    >
                      <input
                        type='radio'
                        name='taxDocumentType'
                        className='accent-black'
                        value={key}
                        checked={selectedTaxDocumentType === key}
                        onChange={() => handleTaxDocumentTypeChange(key)}
                        data-testid={`tax-document-type-${key}`}
                      />
                      {TAX_DOCUMENT_TYPES[key]}
                    </label>
                  ))}
                </div>
                {errors.taxDocumentType && (
                  <span className='text-xs absolute top-0 right-0 p-2 text-red-300'>
                    {errors.taxDocumentType.message}
                  </span>
                )}
              </div>
            </div>
          )}

          <div
            className={`${isBilling
              ? 'justify-between my-2'
              : 'justify-end mt-3 mb-0 md:hidden'
              } flex row items-center`}
          >
            <SectionTitle
              title='Datos de facturación'
              iconName='apartment'
              primaryColor={primaryColor}
              className={`${isBilling ? 'flex' : 'hidden'
                } items-center pl-2 text-sm font-medium md:text-[11px] md:font-bold`}
            />

            <div className={`${useBillingData && !showTaxDocumentSelector ? '' : 'hidden'} md:hidden`}>
              <Button
                label={isBilling ? 'Cancelar' : 'Agregar datos de Facturación'}
                LeftIcon={
                  <Icon name='edit' className={`mr-1 text-sm ${Object.keys(CUSTOM_BILLING_BUTTON).includes(companyName) && 'fill-white'}`} width={9} />
                }
                onClick={() => {
                  setIsBilling(!isBilling)
                }}
                className='bg-slate-100 px-4 py-1 rounded-2xl text-[11px] font-light flex items-center h-[23px]'
                testId='billing-data'
                customStyle={Object.keys(CUSTOM_BILLING_BUTTON).includes(companyName) ? { backgroundColor: CUSTOM_BILLING_BUTTON[companyName], color: 'white' } : {}}
              />
            </div>
          </div>
          <div
            className={`${isBilling ? 'block' : 'hidden'
              } flex flex-wrap justify-between`}
          >
            {Object.keys(billingInfo).map(key => (
              <Input
                key={key}
                {...billingInfo[key]}
                containerClass={`mb-3 md:mb-1.5 bg-white border md:bg-white ${key === 'corporateName' || key === 'address' || key === 'category'
                  ? 'md:w-7/12'
                  : key === 'corporateRut' || key === 'district' || key === 'corporatePhone'
                    ? 'md:w-2/5'
                    : ''
                  }`}
                innerRef={billingInfo[key].ref}
                type={key === 'corporatePhone' ? 'tel' : 'text'}
                errors={errors[key] || watch(key) === ''}
                customErrors={customErrors[key]}
                secondaryColor={secondaryColor}
                testId={key}
              />
            ))}
          </div>
          <div className='hidden md:flex md:items-start  md:flex-row md:justify-between'>
            <div className='flex flex-col md:pl-3 w-full items-start'>
              <div className={`flex pt-5 justify-center terms-checkbox ${companyName === 'SELFIE' ? '' : 'hidden'}`}>
                <div className='text-sm'>
                  &nbsp; ¿Tienes cuenta? Ingresa<a className="no-underline pl-1" target="_blank" rel="noreferrer" href="https://www.selfie.cl/account/login?return_url=%2Faccount"><b>acá</b></a>
                </div>
              </div>
              <div className={`flex pt-3 justify-center terms-checkbox ${companyName === 'SCJJSPA' ? '' : 'hidden'}`}>
                <input
                  type='checkbox'
                  name='saveData'
                  onChange={saveData => handleCheckbox(saveData)}
                  checked={saveData}
                  onInvalid={(e) => e.target.setCustomValidity('Debes aceptar los T&C para continuar')}
                  onInput={(e) => e.target.setCustomValidity('')}
                  data-testid="term-check"
                />
                <div className='text-xs flex pl-1 font-inter'>
                  <Icon name='flash' className='fill-slate-400 w-[9px] mr-1 hidden' />{' '}
                  Acepto los <a className="no-underline pl-1" target="_blank" rel="noreferrer" href="https://japijane.cl/pages/despachos"> <b>términos y condiciones</b></a>
                </div>
              </div>
              {errors.saveData && (errors.saveData || !saveData) && <span className='text-red-400 text-xs pl-4'>Debes aceptar T&C para continuar</span>}
            </div>
            <div className='flex justify-center pb-2'>
              <Button
                role='submit'
                className='bg-black text-white text-base rounded-3xl mt-3 mb-2 pb-1 justify-center disabled:bg-slate-100 disabled:text-gray-500 md:h-[37px] md:w-[149px] md:px-[15px] md:my-0.5'
                label='Continuar'
                disabled={Object.keys(watch()).some(
                  key =>
                    (watch(key) === '' &&
                      !Object.keys(billingInfo).includes(key)) ||
                    (isBilling && watch(key) === '')
                )}
                loading={loadingButton}
                customStyle={missingTaxDocumentType ? DISABLED_LOOK_STYLE : { backgroundColor: secondaryColor }}
                testId='go-delivery'
              />
            </div>
          </div>
        </div>
        <div className='mt-9 w-full bg-white flex flex-col justify-between'>
          <div className='md:hidden'>
            <div className='flex flex-col'>
              <div className={`flex pt-5 justify-center terms-checkbox ${companyName === 'SCJJSPA' ? '' : 'hidden'}`}>
                <input
                  type='checkbox'
                  name='saveData'
                  onChange={saveData => handleCheckbox(saveData)}
                  checked={saveData}
                  onInvalid={(e) => e.target.setCustomValidity('Debes aceptar los T&C para continuar')}
                  onInput={(e) => e.target.setCustomValidity('')}
                  data-testid="term-check"
                />
                <div className='text-sm'>
                  &nbsp; Acepto los <a className="no-underline pl-1" target="_blank" rel="noreferrer" href="https://japijane.cl/pages/despachos"> <b>términos y condiciones</b></a>
                </div>
              </div>
              <div className={`flex pt-5 justify-center terms-checkbox ${companyName === 'SELFIE' ? '' : 'hidden'}`}>
                <div className='text-sm'>
                  &nbsp; ¿Tienes cuenta? Ingresa<a className="no-underline pl-1" target="_blank" rel="noreferrer" href="https://www.selfie.cl/account/login?return_url=%2Faccount"><b>acá</b></a>
                </div>
              </div>
            </div>
            <div className='flex justify-center'>
              <Button
                role='submit'
                className='h-[3rem] bg-black text-white text-base rounded-lg mt-4 w-[90vw] justify-center disabled:bg-slate-100 disabled:text-gray-500'
                label='Continuar'
                disabled={Object.keys(watch()).some(
                  key =>
                    (watch(key) === '' &&
                      !Object.keys(billingInfo).includes(key)) ||
                    (isBilling && watch(key) === '')
                )}
                loading={loadingButton}
                customStyle={missingTaxDocumentType ? DISABLED_LOOK_STYLE : { backgroundColor: secondaryColor }}
                testId='go-delivery'
              />
            </div>
            <div className='md:hidden flex justify-center pt-2 text-sm text-slate-300'>
              100% Seguro + Rápido y Facil. Powered by
              <a
                href='https://pinflag.cl'
                target='_blank'
                style={{ textDecoration: 'none' }}
                rel='noreferrer'
              >
                <span className='font-bold'>&nbsp;Pinflag</span>
              </a>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}

UserForm.propTypes = {
  className: PropTypes.string
}

export default UserForm
